import { ListChecks, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { JobsFilter, JobStatus, JobSummary, listJobs } from "../api/jobs";
import { listScripts, ScriptSummary } from "../api/scripts";
import { listPhones, Phone } from "../api/phones";
import { LaunchJobModal } from "../components/LaunchJobModal";
import { StatusBadge } from "../components/StatusBadge";
import { relativeTime } from "../utils/format";

export function JobsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const STATUS_OPTIONS: { value: JobStatus | ""; label: string }[] = [
    { value: "", label: t("common.all") },
    { value: "running", label: t("status.running") },
    { value: "paused", label: t("status.paused") },
    { value: "completed", label: t("status.completed") },
    { value: "cancelled", label: t("status.cancelled") },
    { value: "error", label: t("status.error") },
  ];
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<JobStatus | "">("");

  // État de la modale "Nouveau job" : on charge scripts + phones une fois
  // ouverte, l'user choisit, on délègue ensuite à LaunchJobModal.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerScripts, setPickerScripts] = useState<ScriptSummary[]>([]);
  const [pickerPhones, setPickerPhones] = useState<Phone[]>([]);
  const [pickedScript, setPickedScript] = useState<ScriptSummary | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  async function openPicker() {
    setPickerError(null);
    try {
      const [s, p] = await Promise.all([listScripts(), listPhones()]);
      setPickerScripts(s.filter((x) => x.is_active));
      setPickerPhones(p);
      setPickerOpen(true);
    } catch (e) {
      setPickerError(e instanceof Error ? e.message : t("common.error"));
    }
  }

  async function reload() {
    try {
      setLoading(true);
      const filter: JobsFilter = { limit: 100 };
      if (statusFilter) filter.status = statusFilter;
      const data = await listJobs(filter);
      setJobs(data.jobs);
      setTotal(data.total);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // Auto-refresh toutes les 5s pour suivre les runs en cours
    const id = window.setInterval(reload, 5000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
            <ListChecks className="size-6 text-velvet-400" />
            {t("jobs.title")}
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            {t(total > 1 ? "jobs.countOther" : "jobs.countOne", { count: total })}{" "}
            {t("jobs.countSuffix")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-ink-500">{t("common.filter")}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value || "") as JobStatus | "")}
            className="px-3 py-2 rounded-lg bg-ink-700/60 text-white text-sm border border-ink-600 focus:border-velvet-500 focus:outline-none"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => void openPicker()}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium transition-colors"
          >
            <Plus className="size-4" />
            {t("jobs.newBtn")}
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-ink-700 bg-ink-800/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-700/40 text-ink-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">{t("jobs.columns.name")}</th>
              <th className="text-left px-4 py-3">{t("jobs.columns.script")}</th>
              <th className="text-left px-4 py-3">{t("jobs.columns.phone")}</th>
              <th className="text-left px-4 py-3">{t("jobs.columns.mode")}</th>
              <th className="text-left px-4 py-3">{t("jobs.columns.progress")}</th>
              <th className="text-left px-4 py-3">{t("jobs.columns.status")}</th>
              <th className="text-left px-4 py-3">{t("jobs.columns.created")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700/50">
            {loading && jobs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-ink-500">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && jobs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-ink-500">
                  {t("jobs.empty")}
                </td>
              </tr>
            )}
            {jobs.map((j) => {
              const progressText = j.max_runs
                ? `${j.current_run_count} / ${j.max_runs}`
                : `${j.current_run_count} / ∞`;
              const progressPct = j.max_runs
                ? Math.min(100, Math.round((j.current_run_count / j.max_runs) * 100))
                : null;
              return (
                <tr
                  key={j.id}
                  onClick={() => navigate(`/jobs/${j.id}`)}
                  className="hover:bg-ink-700/20 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{j.name}</div>
                    {(j.success_count > 0 || j.failed_count > 0) && (
                      <div className="text-xs text-ink-500 mt-0.5">
                        <span className="text-emerald-400">{j.success_count} ✓</span>
                        {" / "}
                        <span className="text-red-400">{j.failed_count} ✗</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-400">{j.script_name ?? "—"}</td>
                  <td className="px-4 py-3 text-ink-400">{j.phone_name ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-md bg-ink-700/60 text-xs text-ink-300">
                      {j.mode}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[150px]">
                    <div className="text-ink-300 text-xs mb-1">{progressText}</div>
                    {progressPct !== null && (
                      <div className="h-1.5 rounded-full bg-ink-700 overflow-hidden">
                        <div
                          className="h-full bg-velvet-500"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={j.status} />
                  </td>
                  <td className="px-4 py-3 text-ink-400">{relativeTime(j.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pickerError && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {pickerError}
        </div>
      )}

      {/* Étape 1 du picker : choix du script (les employees ne voient que
          name/description, jamais le content). On délègue ensuite à
          LaunchJobModal pour le choix tel + mode + max_runs. */}
      {pickerOpen && !pickedScript && (
        <ScriptPickerModal
          scripts={pickerScripts}
          phones={pickerPhones}
          onClose={() => setPickerOpen(false)}
          onPicked={(s) => setPickedScript(s)}
        />
      )}

      {/* Étape 2 : config du job (réutilise la modale existante). */}
      {pickerOpen && pickedScript && (
        <LaunchJobModal
          script={pickedScript}
          onClose={() => {
            setPickerOpen(false);
            setPickedScript(null);
          }}
          onLaunched={() => {
            setPickerOpen(false);
            setPickedScript(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

function ScriptPickerModal({
  scripts, phones, onClose, onPicked,
}: {
  scripts: ScriptSummary[];
  phones: Phone[];
  onClose: () => void;
  onPicked: (s: ScriptSummary) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-ink-800 border border-ink-700 rounded-xl p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-white mb-4">{t("jobs.picker.title")}</h2>

        {phones.length === 0 ? (
          <div className="text-sm text-amber-400 mb-4">
            {t("jobs.picker.noPhones")}
          </div>
        ) : null}

        {scripts.length === 0 ? (
          <div className="text-sm text-ink-500">{t("jobs.picker.noScripts")}</div>
        ) : (
          <div className="border border-ink-600 rounded-md max-h-72 overflow-auto divide-y divide-ink-700">
            {scripts.map((s) => (
              <button
                key={s.id}
                onClick={() => onPicked(s)}
                className="w-full text-left px-3 py-2.5 hover:bg-ink-700/40 transition-colors"
              >
                <div className="text-sm text-white font-medium">{s.name}</div>
                {s.description && (
                  <div className="text-xs text-ink-500 mt-0.5 line-clamp-2">
                    {s.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm text-ink-400 hover:bg-ink-700/50"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
